import type { Listing, ListingStatusAction } from './listings';

/** What an author can do to a listing's publish state and status (016 research C1). */
export type ListingAction =
  | 'publishNow'
  | 'schedule'
  | 'changeSchedule'
  | 'clearSchedule'
  | 'markSold'
  | 'markForSale'
  | 'withdraw'
  | 'republish'
  | 'backToDraft';

/**
 * The actions a listing offers, from its state. `publishState` is only ever
 * set for the author, so anyone else is offered nothing.
 */
export function availableActions(listing: Listing): ListingAction[] {
  switch (listing.publishState) {
    case 'draft':
      return ['publishNow', 'schedule'];
    case 'scheduled':
      return ['publishNow', 'changeSchedule', 'clearSchedule'];
    case 'published':
      if (listing.status === 'sold') return ['markForSale', 'withdraw', 'backToDraft'];
      if (listing.status === 'withdrawn') return ['republish', 'backToDraft'];
      return ['markSold', 'withdraw', 'backToDraft'];
    default:
      return [];
  }
}

const STATUS_ACTION: Record<ListingAction, ListingStatusAction> = {
  publishNow: 'publishNow',
  schedule: 'schedule',
  changeSchedule: 'schedule',
  clearSchedule: 'unpublish',
  markSold: 'markSold',
  markForSale: 'markForSale',
  withdraw: 'withdraw',
  republish: 'republish',
  backToDraft: 'unpublish',
};

/** The contract's action for an offered one. */
export function toStatusAction(action: ListingAction): ListingStatusAction {
  return STATUS_ACTION[action];
}

/** The badge a listing wears: Sold for everyone; Draft, Scheduled and Withdrawn for its author. */
export type ShownState =
  | { kind: 'sold' }
  | { kind: 'draft' }
  | { kind: 'scheduled'; at: string }
  | { kind: 'withdrawn' }
  | null;

export function shownState(listing: Listing): ShownState {
  if (listing.publishState === 'draft') return { kind: 'draft' };
  if (listing.publishState === 'scheduled' && listing.publishAt) {
    return { kind: 'scheduled', at: listing.publishAt };
  }
  if (listing.status === 'withdrawn') return { kind: 'withdrawn' };
  if (listing.status === 'sold') return { kind: 'sold' };
  return null;
}

/** The listing's web page: only people who can already see it can open it. */
export function listingLink(id: string, origin: string): string {
  return `${origin}/listings/${id}`;
}
